import {GeometryType} from '../enums/geometry-type.enum';
import {BaseGeometry} from './base-geometry';
import {Label} from './label';
import {SettingsService} from '../services/settings.service';
import {Logger} from '../../common/utils/logger';
import {CommonUtil} from '../../common/utils/common.util';

export abstract class BasePoint extends BaseGeometry {
  latLng: google.maps.LatLngLiteral;
  /**
   * Elevation above SEA LEVEL, as imported. Written to GeoJSON, CSV and MMP as
   * the third coordinate, so it is not free to reuse for anything else.
   */
  altitude = 0.0;
  /**
   * Height above the GROUND, for the 3D view only.
   *
   * Deliberately separate from `altitude`: that one is sea level and travels
   * in and out of files, and overloading it would quietly corrupt every export.
   * This one exists so a corner can be pinned at eaves height and the next at
   * ridge height, which is what makes a sloped roof plane rather than a flat
   * outline lying on the building.
   */
  altitude3d = 0.0;
  description = '';
  label: Label;
  showInReport = true;
  showLabelOnMap = false;
  descriptionLabelSize = 13;
  private labelPosition: google.maps.LatLngLiteral;

  set showLabelBackground(show: boolean) {
    this.label.hasBackground = show;
  }

  constructor(
    id: string,
    type: GeometryType
  ) {
    super(id, type);
    this.label = new Label(this.id);
    if (type === GeometryType.TYPE_SPOT) {
      this.descriptionLabelSize = SettingsService.settings.spotLabelSize;
    } else {
      this.descriptionLabelSize = SettingsService.settings.descriptionLabelSize;
    }
  }

  get lat() {
    return this.latLng.lat;
  }

  get lng() {
    return this.latLng.lng;
  }

  moveOffset(offsetX: number, offsetY: number) {
    this.move({
      lat: this.latLng.lat + offsetX,
      lng: this.latLng.lng + offsetY
    });
  }

  move(newLatLng: google.maps.LatLngLiteral) {
    const offsetX = newLatLng.lat - this.lat;
    const offsetY = newLatLng.lng - this.lng;
    if (newLatLng.lat !== this.lat || newLatLng.lng !== this.lng) {
      this.altitude = 0;
    }
    this.latLng = {lat: newLatLng.lat, lng: newLatLng.lng};

    const labelPos = this.getDescriptionPosition();
    this.updateLabel({
      lat: labelPos.lat + offsetX,
      lng: labelPos.lng + offsetY
    });

    this.update();
  }

  getDescriptionPosition(): google.maps.LatLngLiteral {
    if (this.labelPosition) {
      return this.labelPosition;
    }
    if (this.label && this.label.latLng) {
      return this.label.latLng;
    }
    return this.latLng;
  }

  updateLabel(position?: google.maps.LatLngLiteral) {
    // if (!this.label) {
    // }
    if (!position) {
      this.label.latLng = this.getDescriptionPosition();
    } else {
      this.label.latLng = position;
      this.labelPosition = position;
    }
    this.updateLabelSize();
    this.label.textSize = this.descriptionLabelSize;
    this.label.text = this.description;
  }

  updateLabelSize() {
    if (this.type === GeometryType.TYPE_SPOT) {
      this.descriptionLabelSize = SettingsService.settings.spotLabelSize;
    } else {
      this.descriptionLabelSize = SettingsService.settings.descriptionLabelSize;
    }
  }

  equalsPosition(other: BasePoint): boolean {
    return this.lat === other.lat && this.lng === other.lng;
  }

  equalsLatLng(other: google.maps.LatLngLiteral): boolean {
    return this.lat === other.lat && this.lng === other.lng;
  }
}
